"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * 進捗グラフ (React Flow) 専用のエラーバウンダリ。
 * ライブラリ内部の描画例外 (例: Maximum update depth) がページ全体を
 * 巻き込まないよう、グラフ領域だけをフォールバック表示に置き換える。
 */
export class GraphErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    // **どのコンポーネントで起きたかを捨てない。** 本番ビルドの React は
    // エラー本文を番号に畳む（例: Maximum update depth を #185 と出す）ので、
    // error だけを見ても発生源が分からない。React が渡す component stack が
    // 唯一の手がかりになる。実際に「更新が止まらない」で調べたとき、これが
    // 無いために原因を絞り込めなかった。
    console.error(
      "[pipeline-graph] rendering failed:",
      error,
      "\ncomponentStack:",
      errorInfo?.componentStack ?? "(none)",
    );
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          進捗グラフの描画でエラーが発生したため、グラフ表示を停止しました
          （チェックの実行・結果には影響ありません）。再読み込みで復帰する場合があります。
        </div>
      );
    }
    return this.props.children;
  }
}
